import React, { FC } from 'react';
import { truncate } from '@blink/components/src/utils';
import { ReactComponent as CopyIcon } from '@blink/assets/src/icons/copy.svg';

import { BorrowStatusType } from '@/shared/api/borrow';
import { AssetIcon, Button, ButtonVariant, SkeletonInfo } from '@/shared/ui';
import { cn } from '@/shared/lib/tailwind-merge';
import { useCopyToClipboard } from '@/shared/hooks/useCopyToClipboard';

type UserBorrowsCardSkeleton = {
    connectedMarginAccount?: string;
    status: BorrowStatusType;
    address: string;
    symbol: string;
};

export const UserBorrowsCardSkeleton: FC<UserBorrowsCardSkeleton> = ({
    address,
    symbol,
    connectedMarginAccount,
    status,
}) => {
    const isBorrowConnected = connectedMarginAccount === address;
    const handleCopyAddress = useCopyToClipboard(address);

    return (
        <div
            className={cn(
                `
                    min-w-[362px]
                    rounded 
                    border border-opaque 
                    text-content-secondary 
                    transition-shadow duration-300 ease-in-out 
                    hover:text-content-secondary 
                    hover:shadow-[0_0_0_0px_var(--color-border-opaque),0_0_0_2px_var(--color-border-accent)]
                    flex py-3 px-5 flex-col items-start flex-shrink-0
                `,
                isBorrowConnected && 'border border-opaque',
                status !== BorrowStatusType.OPENED &&
                    'border border-opaque hover:border-opaque hover:cursor-auto',
            )}
        >
            <div className='flex py-3 px-0 justify-between items-start self-stretch'>
                <div className='flex gap-3'>
                    <AssetIcon assetName={symbol} className='w-8 h-8' />
                    <div>
                        <div className='text-content-primary text-[14px] font-semibold leading-5'>
                            {symbol}
                        </div>
                        <div className='flex text-[12px] font-normal justify-center items-center gap-2'>
                            {truncate({ value: address, startSymbols: 6 })}
                            <Button
                                variant={ButtonVariant.Ghost}
                                onClick={handleCopyAddress}
                                className='p-0 h-5'
                            >
                                <CopyIcon className='!w-[16px] !h-[16px]' />
                            </Button>
                        </div>
                    </div>
                </div>
                <div
                    className={`flex min-w-[24px] min-h-[24px] py-[2px] px-[6px] gap-[4px] justify-center items-center rounded-[320px]`}
                />
            </div>
            <div className='flex justify-between py-3 items-start self-stretch gap-3'>
                <div className='flex flex-col text-sm items-start gap-[2px] flex-[1_0_0]'>
                    <div className='text-content-fourth text-sm font-normal'>Maint. Margin</div>
                    <div className='text-content-primary text-md font-semibold'>
                        <SkeletonInfo />
                    </div>
                </div>

                <div className='flex flex-col text-sm items-start gap-[2px] flex-[1_0_0]'>
                    <div className='text-content-fourth text-sm font-normal'>Borrowed Amount</div>
                    <div className='text-content-primary text-md font-semibold'>
                        <SkeletonInfo />
                    </div>
                </div>

                <div className='flex flex-col text-sm items-start gap-[2px] flex-[1_0_0]'>
                    <div className='text-content-fourth text-sm font-normal'>Interest Accrued</div>
                    <div className='text-content-primary text-md font-semibold'>
                        <SkeletonInfo />
                    </div>
                </div>
            </div>
            <div className='flex py-3 items-start gap-7 self-stretch'>
                <div className='flex flex-col items-start gap-[2px] flex-[1_0_0]'>
                    <div className='text-content-fourth text-sm font-normal leading-5 tracking-[-0.36px]'>
                        Pool
                    </div>
                    <div className='text-content-primary text-md font-semibold leading-6 tracking-[-0.42px] h-[40px]'>
                        <SkeletonInfo />
                    </div>
                </div>
            </div>
        </div>
    );
};
